import React, { useState } from 'react';
import { Play, X } from 'lucide-react';

const VideoGallery = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  const videos = [
    {
      id: 1,
      title: 'Video 1',
      description: 'Figure skating training session',
      thumbnail: 'https://customer-assets.emergentagent.com/job_ice-maestro/artifacts/gjcmroh4_VASSILEVA%20%281%29.jpg',
      videoUrl: 'https://customer-assets.emergentagent.com/job_ice-maestro/artifacts/0109wdyj_0-02-05-e4c1a1a4249e54f8398240a1462ccaa56bc7d6c9dfbc4b1d660b2c8dc6196a6d_76b560c033af21d6.mp4',
      category: 'Training',
      isDirectVideo: true
    },
    {
      id: 2,
      title: 'Video 2',
      description: 'Professional coaching and training',
      thumbnail: 'https://customer-assets.emergentagent.com/job_ice-maestro/artifacts/saeewtml_viber_image_2025-12-29_15-59-09-727.jpg',
      videoUrl: 'https://customer-assets.emergentagent.com/job_ice-maestro/artifacts/uf4l7ocl_VID_20250819_120708.mp4',
      category: 'Coaching',
      isDirectVideo: true
    },
    {
      id: 3,
      title: 'Video 3',
      description: 'On-ice training and technique',
      thumbnail: 'https://customer-assets.emergentagent.com/job_ice-maestro/artifacts/onxyn2t5_viber_image_2025-12-29_16-01-54-909.jpg',
      videoUrl: 'https://customer-assets.emergentagent.com/job_ice-maestro/artifacts/r0qsf92o_VID_20250819_165452.mp4',
      category: 'Practice',
      isDirectVideo: true
    },
    {
      id: 4,
      title: 'Video 4',
      description: 'Advanced skating techniques and skills',
      thumbnail: 'https://customer-assets.emergentagent.com/job_ice-maestro/artifacts/f97s7fdd_IMG_20240811_101147.jpg',
      videoUrl: 'https://customer-assets.emergentagent.com/job_ice-maestro/artifacts/d030fzpj_VID_20240805_180529.mp4',
      category: 'Skills',
      isDirectVideo: true
    }
  ];

  return (
    <section id="videos" className="py-24 bg-slate-800">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block px-4 py-2 bg-sky-600 text-white rounded-full text-sm font-semibold mb-6">
            Video Gallery
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Watch & Learn
          </h2>
          <p className="text-xl text-slate-300">
            Experience the artistry and athleticism of figure skating through competition performances, training sessions, and student showcases.
          </p>
        </div>

        {/* Video Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video) => (
            <div
              key={video.id}
              className={`group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 ${video.videoUrl ? 'cursor-pointer' : 'cursor-default opacity-75'}`}
              onClick={() => video.videoUrl && setSelectedVideo(video)}
            >
              {/* Thumbnail */}
              <div className="relative aspect-video">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent"></div>
                
                {/* Play Button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-sky-600 rounded-full p-4 transform group-hover:scale-110 transition-transform duration-300 shadow-xl">
                    <Play className="w-8 h-8 text-white fill-white" />
                  </div>
                </div>

                {/* Category Badge */}
                <div className="absolute top-3 right-3">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-slate-700">
                    {video.category}
                  </span>
                </div>
              </div>

              {/* Video Info */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-white font-bold text-lg mb-1">
                  {video.title}
                </h3>
                <p className="text-slate-200 text-sm">
                  {video.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Note about videos */}
        <div className="mt-12 text-center">
          <p className="text-slate-400 text-sm">
            More videos coming soon. You can add YouTube or Vimeo links, or upload more video files.
          </p>
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              className="absolute -top-12 right-0 text-white hover:text-sky-400 transition-colors"
              onClick={() => setSelectedVideo(null)}
            >
              <X size={32} />
            </button>

            {/* Video Player */}
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
              {selectedVideo.isDirectVideo ? (
                <video
                  src={selectedVideo.videoUrl}
                  controls
                  className="w-full h-full"
                  autoPlay
                >
                  Your browser does not support the video tag.
                </video>
              ) : selectedVideo.videoUrl ? (
                <iframe
                  src={selectedVideo.videoUrl}
                  title={selectedVideo.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white">
                  <p className="text-lg">Video coming soon</p>
                </div>
              )}
            </div>

            {/* Video Info */}
            <div className="mt-6 text-center">
              <div className="text-sky-400 text-sm font-semibold mb-2">
                {selectedVideo.category}
              </div>
              <h3 className="text-white text-2xl font-bold mb-2">
                {selectedVideo.title}
              </h3>
              <p className="text-slate-300">
                {selectedVideo.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default VideoGallery;
